import React, { useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';
import { Button, DatePicker, Input, Select, Table, Tag } from 'antd';
import { useSelector } from 'react-redux';
import {
  ACTION_LOGS,
  DECODED_ACCESS_TOKEN,
  NOTIFIES,
} from '../../../constants/system.keys';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getExportReportLogsApi } from '../../../api/customerApi';
import storageService from '../../../services/storage.service';
import colorsDark from '../../../assets/colors/dark';
import { openMessage } from '../../../redux/actions';

const ActionTagColors = (actionName) => {
  switch (actionName) {
    case 'Delete':
      return colorsDark.$errors;

    case 'Add':
      return colorsDark.$online;

    case 'Update':
      return colorsDark.$warning;

    default:
      return colorsDark.$offline;
  }
};

export const ActionLog = () => {
  const [dataFilterTypeLog, setDataFilterTypeLog] = useState('Login');
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, 'week'),
    moment(),
  ]);
  const [phonenumber, setPhonenumber] = useState('');
  const { t } = useTranslation();
  const fetchId = useRef(0);
  const [dataState, setDataState] = useState({
    loading: true,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  const namespace = useSelector((state) => state.namespace);

  const fetchLogs = async ({
    action,
    fromTime,
    toTime,
    phonenumber,
    namespace,
  }) => {
    const fetchIdCurrent = ++fetchId.current;
    setDataState({
      ...dataState,
      loading: true,
      page: 1,
    });

    const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
    const dataQuery = {
      customerNamespace: namespace,
      action,
      fromTime,
      toTime,
      isDownload: 0,
    };

    if (phonenumber) {
      dataQuery.phonenumber = phonenumber;
    }

    const dataHeader = {
      'Content-Type': 'application/json',
      namespace,
    };

    return getExportReportLogsApi(token, dataHeader, dataQuery).then((res) => {
      if (fetchIdCurrent !== fetchId.current) {
        return res;
      }

      const logs = res.data.actionLogs.reverse() || [];

      setDataState({
        ...dataState,
        data: logs,
        total: logs.length,
        loading: false,
      });

      return res;
    });
  };

  useEffect(() => {
    if (!namespace) {
      return;
    }

    fetchLogs({
      customerNamespace: namespace,
      action: dataFilterTypeLog,
      fromTime: dateRange[0].valueOf(),
      toTime: dateRange[1].valueOf(),
      namespace,
    });
  }, [namespace]);

  const handleChangeSelectTypeLog = (value) => {
    setDataFilterTypeLog(value);
    fetchLogs({
      customerNamespace: namespace,
      action: value,
      fromTime: dateRange[0].valueOf(),
      toTime: dateRange[1].valueOf(),
      namespace,
      phonenumber,
    });
  };
  const handleDateChange = (dates) => {
    setDateRange(dates);
    fetchLogs({
      customerNamespace: namespace,
      action: dataFilterTypeLog,
      fromTime: dates[0].valueOf(),
      toTime: dates[1].valueOf(),
      namespace,
      phonenumber,
    });
  };

  const handleSearchLogs = (value) => {
    setPhonenumber(value);
    fetchLogs({
      customerNamespace: namespace,
      action: dataFilterTypeLog,
      fromTime: dateRange[0].valueOf(),
      toTime: dateRange[1].valueOf(),
      namespace,
      phonenumber: value,
    });
  };

  const paginatedData = useMemo(() => {
    return dataState.data.slice(
      (dataState.page - 1) * dataState.pageSize,
      dataState.page * dataState.pageSize
    );
  }, [dataState.data, dataState.page, dataState.pageSize]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadLogs = () => {
    setIsDownloading(true);
    const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
    const dataQuery = {
      customerNamespace: namespace,
      action: dataFilterTypeLog,
      fromTime: dateRange[0].valueOf(),
      toTime: dateRange[1].valueOf(),
      isDownload: 1,
    };

    if (phonenumber) {
      dataQuery.phonenumber = phonenumber;
    }

    const dataHeader = {
      namespace,
    };

    return getExportReportLogsApi(token, dataHeader, dataQuery)
      .then((dataRes) => {
        setTimeout(() => {
          if (dataRes?.data) {
            const buttonElement = document.createElement('a');
            buttonElement.href = `data:text/csv;charset=utf-8,${encodeURI(
              dataRes.data
            )}`;
            buttonElement.target = '_blank';
            buttonElement.download = `actions-log.${moment(dateRange[0]).format(
              'DD_MM_YYYY'
            )}-${moment(dateRange[1]).format('DD_MM_YYYY')}.csv`;
            buttonElement.click();
          } else {
            openMessage(NOTIFIES.WARNING, 'No data export');
          }
          setIsDownloading(false);
        }, 800);
      })
      .catch(() => {
        setIsDownloading(false);
      });
  };

  return (
    <div className="action-log">
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingBottom: '5px',
          gap: '10px',
        }}
      >
        <DatePicker.RangePicker
          value={dateRange}
          onChange={handleDateChange}
          format="YYYY-MM-DD"
        />
        <Select
          defaultValue={dataFilterTypeLog}
          style={{ width: 235 }}
          onChange={handleChangeSelectTypeLog}
        >
          {Object.keys(ACTION_LOGS)?.map((item) => (
            <Select.Option value={ACTION_LOGS[item]}>
              {t(ACTION_LOGS[item]?.replaceAll('_', ' '))}
            </Select.Option>
          ))}
        </Select>
        <Input.Search
          placeholder={t('Search by phone number')}
          onSearch={handleSearchLogs}
          style={{
            width: 240,
          }}
          allowClear
        />
        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownloadLogs}
          loading={isDownloading}
        >
          Download
        </Button>
      </div>
      <Table
        dataSource={paginatedData.map((log, key) => ({
          ...log,
          key,
        }))}
        pagination={{
          total: dataState.total,
          pageSize: dataState.pageSize,
          current: dataState.page,
          onChange: (page, pageSize) => {
            setDataState({
              ...dataState,
              page,
              pageSize,
            });
          },
        }}
        loading={dataState.loading}
        columns={[
          {
            title: t('Phone number'),
            dataIndex: 'phonenumber',
            key: 'log_phonenumber',
          },
          {
            title: t('Full name'),
            dataIndex: 'fullname',
            key: 'log_fullname',
          },
          {
            title: t('Email'),
            dataIndex: 'email',
            key: 'log_email',
            render: (emails) => (
              <span>{emails?.split(';')?.join('; ') ?? ''}</span>
            ),
          },
          {
            title: t('IP Address'),
            dataIndex: 'ipAddress',
            key: 'log_ipAddress',
          },
          {
            title: t('Action'),
            dataIndex: 'action',
            key: 'log_action',
            render: (action) => (
              <Tag color={ActionTagColors(action)}>
                {action && action.toUpperCase()}
              </Tag>
            ),
          },
          {
            title: t('Target'),
            key: 'log_namespace',
            dataIndex: 'content',
          },
          {
            title: t('Created at'),
            width: 200,
            key: 'log_activatedAt',
            dataIndex: 'actionAt',
            render: (time) => moment(time).format('lll'),
          },
        ]}
      />
    </div>
  );
};
